import React, { useEffect, useState } from 'react';
import { fetchAnswers } from '../actions';
import { Collapse } from 'ui';
import {
  GECKO_CONSTS,
  GeckoTable,
  GeckoPagination,
  GeckoToastService,
  GeckoAlert,
  GeckoModalService,
  GeckoModal,
  GeckoDialogService,
  GeckoIcon,
  OverlayTrigger,
  Tooltip,
} from 'ui';
import GeckoLocation from 'services/gecko-location';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import BotTabTableContainer from '../BotTabTableContainer';
import ConversationSnippet from '../ConversationSnippet';
import ProgressRing from 'components/ProgressRing';
import StateRing from 'components/StateRing';
import { BotTagDisplay } from '../BotTagManager';
import QuickSearch from 'components/BuildableFilter/QuickSearch';
import FilterBuilder from 'components/BuildableFilter/FilterBuilder';
import NormalisedGeckoField from 'components/NormalisedGeckoField';
import DeltaSummary from '../Answers/Imports/BotAnswersImportReview/DeltaSummary';
import { useRedux } from 'services/gecko-store/redux';
import { useBot } from '../hooks';
import { tagOptionsSelector } from 'reducers/bot_tags_selector';
import AnswersTableQuickFilters from './AnswersTableQuickFilters';
import TableTeamSelect from 'components/TableTeamSelect';
import TeamSelect from 'components/TeamSelect';
import classNames from 'classnames/bind';
import useDebounce from '../../../utilities/useDebounce';
import GeckoDate from 'services/gecko-date';

function answersReduxMapper(state) {
  return {
    page: state.botsRoute.answers.page,
    orderBy: state.botsRoute.answers.orderBy,
    order: state.botsRoute.answers.order,
    total: state.botsRoute.answers.total,
    search: state.botsRoute.answers.search,
    responses: state.botsRoute.answers.ids.map(
      (id) => state.botsRoute.answersById[id]
    ),
    filters: state.botsRoute.answers.filters,
    skills: state.skills,
    teams: state.teams,
    tagChoices: tagOptionsSelector(state),
  };
}

function Answers({ botId }) {
  const {
    page,
    orderBy,
    order,
    total,
    search,
    responses,
    filters,
    skills,
    tagChoices,
  } = useRedux(answersReduxMapper);
  const agents = useRedux((state) => state.agents);
  const [showFilters, setShowFilters] = useState(filters.length !== 0);
  const [pickedFilters, setPickedFilters] = useState(filters);
  const debouncedFilters = useDebounce(pickedFilters);
  const [bot] = useBot(botId);

  useEffect(() => {
    fetchAnswers({
      page: page || 1,
      order: 'DESC',
      orderBy: 'createdAt',
      botId: botId,
      search: '',
      filters: filters,
    });
  }, []);

  useEffect(() => {
    fetchAnswers({
      page: 1,
      botId: botId,
      order: order,
      orderBy: orderBy,
      search: search,
      filters: debouncedFilters,
    });
  }, [debouncedFilters]);

  if (!bot) {
    return null;
  }

  const cloneAnswer = (answer) => {
    return GeckoLocation.goTo(`/bots/${botId}/answers/clone/${answer.id}`);
  };

  const deleteAnswer = (answer) => {
    return new GeckoDialogService()
      .confirm(`Are you sure you want to delete this answer?`)
      .then((response) => {
        if (response) {
          return GeckoRequest.delete(`/bots/${botId}/answers/${answer.id}`)
            .then(() => {
              new GeckoToastService().success(
                `${answer.name} has been deleted`
              );
              fetchAnswers({
                page: page,
                botId: botId,
                orderBy: orderBy,
                order: order,
                filters: filters,
              });
            })
            .catch(renderError);
        }
        return false;
      })
      .catch(renderError);
  };

  const updateTeams = async (teamIds, answerId) => {
    await GeckoRequest.post(`/bots/${botId}/answers/${answerId}/update-team`, {
      teamId: teamIds,
    });
    await fetchAnswers({
      page: page,
      botId: botId,
      order: order,
      orderBy: orderBy,
      search: search,
      filters: filters,
    });
  };

  const cols = [
    {
      key: 'answer_ids',
      align: 'center',
      type: GECKO_CONSTS.TABLE_CELL_CUSTOM,
      render: (_successes, answer) => {
        const onClick = (event) => {
          event.stopPropagation();
          const footerBtns = [
            {
              type: 'success',
              name: 'Open Answer',
              icon: 'far fa-copy',
              handler: () => {
                new GeckoModalService().close();
                GeckoLocation.goTo(`answers/${answer.id}`);
              },
            },
          ];
          return new GeckoModalService().open(() => {
            return (
              <div className="Train-ResolveModal">
                <GeckoModal name="Preview Answer" btns={footerBtns}>
                  <div>
                    <span className="Train-ResolveModal-table-titles">
                      Example Conversation
                    </span>
                    <ConversationSnippet
                      botId={botId}
                      answerId={answer.id}
                      answer={answer}
                    />
                  </div>
                </GeckoModal>
              </div>
            );
          });
        };
        const key = `answer-row-${answer.id}`;
        return (
          <div className="Answer-conversation-link" id={key} onClick={onClick}>
            <OverlayTrigger
              placement="auto"
              overlay={<Tooltip id={key}>Preview Answer</Tooltip>}
            >
              <i id={key}>
                <GeckoIcon icon="far fa-comments" />
              </i>
            </OverlayTrigger>
          </div>
        );
      },
    },
    {
      name: 'Name',
      key: 'name',
      sortable: true,
      type: GECKO_CONSTS.TABLE_CELL_TYPE_TEXT,
    },
    {
      name: 'No. Sample Questions',
      key: 'samples_count',
      sortable: true,
      type: GECKO_CONSTS.TABLE_CELL_TYPE_TEXT,
    },
    {
      name: 'Owner',
      key: 'assigned_team_ids',
      type: GECKO_CONSTS.TABLE_CELL_CUSTOM,
      render: (_successes, answer) => {
        const assignedTeams = answer.assigned_team_ids;
        const onChangeHandler = (teamIds) => updateTeams(teamIds, answer.id);
        return (
          <TableTeamSelect
            onChange={onChangeHandler}
            assignedTeamIds={assignedTeams}
            selectProps={{
              openOnClick: true,
              autoFocus: true,
              openOnFocus: true,
              escapeClearsValue: false,
            }}
          />
        );
      },
    },
    {
      name: 'Times Used',
      key: 'times_used',
      align: 'center',
      sortable: true,
      type: GECKO_CONSTS.TABLE_CELL_TYPE_TEXT,
    },
    {
      name: 'Success Rate',
      key: 'success_rate',
      sortable: true,
      align: 'center',
      type: GECKO_CONSTS.TABLE_CELL_TYPE_CUSTOM,
      render: (successes, answer) => {
        if (answer.times_used === 0) {
          const ringStates = {
            trained: {
              tooltip: 'Untested',
              preset: 'info',
              icon: 'far fa-minus',
            },
          };
          return (
            <StateRing
              states={ringStates}
              radius={21}
              stroke={3}
              defaultState="trained"
            />
          );
        }
        return <ProgressRing radius="21" stroke="3" progress={successes} />;
      },
    },
    {
      name: 'Tags',
      key: 'bot_tag_ids',
      type: GECKO_CONSTS.TABLE_CELL_CUSTOM,
      render: (_tag_ids, answer) => (
        <BotTagDisplay item={answer} controlled={false} />
      ),
    },
    {
      name: 'Last Updated',
      key: 'updatedAt',
      render: (val, answer) => {
        let lastUpdatedByAgent;
        let key;
        if (answer.last_updated_by) {
          lastUpdatedByAgent = agents.find(
            (agent) => agent.id === answer.last_updated_by
          );
          key = `answer-last-updated-by-${answer.id}`;
        }
        return (
          lastUpdatedByAgent ? (
          <OverlayTrigger
            placement="auto"
            overlay={
              <Tooltip
                id={key}
              >{`Edited by ${lastUpdatedByAgent?.full_name}`}</Tooltip>
            }
          >
            <span id={key}>{new GeckoDate(val).LONG_TIME}</span>
          </OverlayTrigger>
        ): null
      )},
      sortable: true,
    },
    {
      type: GECKO_CONSTS.TABLE_CELL_TYPE_DROPDOWN,
      width: '1%',
      items: [
        {
          name: 'Clone',
          handler: cloneAnswer,
        },
        {
          name: 'Delete',
          handler: deleteAnswer,
        },
      ],
    },
  ];

  const handleSearch = (search) => {
    return fetchAnswers({
      page: 1,
      botId: botId,
      orderBy: orderBy,
      order: order,
      search: search,
      filters: filters,
    });
  };

  const rowHandler = (answer) => {
    return GeckoLocation.goTo(`/bots/${botId}/answers/${answer.id}`);
  };

  const onQuickFiltersChange = (newFilters) => {
    setShowFilters(newFilters.length !== 0);
    setPickedFilters(newFilters);
  };

  const renderHeaderChild = () => {
    return (
      <div
        style={{
          paddingLeft: '10px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <AnswersTableQuickFilters
          botId={botId}
          filters={filters}
          onChange={onQuickFiltersChange}
        />
        <QuickSearch
          showExpandButton={true}
          expanded={showFilters}
          onSearch={handleSearch}
          onToggle={() => setShowFilters(!showFilters)}
          filterButtonName=""
        />
      </div>
    );
  };

  const pagination = {
    changeOrder: (sortKey) => {
      if (orderBy === sortKey) {
        fetchAnswers({
          page: page,
          botId: botId,
          orderBy: sortKey,
          order: order === 'ASC' ? 'DESC' : 'ASC',
          search: search,
          filters: filters,
        });
      } else {
        fetchAnswers({
          page: page,
          botId: botId,
          orderBy: sortKey,
          order: 'ASC',
          search: search,
          filters: filters,
        });
      }
    },
    changePage: (newPage) =>
      fetchAnswers({
        botId: botId,
        page: newPage,
        search: search,
        filters: filters,
      }),
    current_page: page,
    order_by: orderBy,
    sort: order,
    total_items: total,
    total_pages: Math.ceil(total / 20),
  };

  const skillChoices = skills
    .filter((skill) => skill.bot_id === botId)
    .map((skill) => {
      return { label: skill.name, value: skill.id };
    });

  const fields = [
    {
      key: 'name',
      type: 'text',
      label: 'Name',
    },
    {
      key: 'team',
      label: 'Team',
      type: 'custom',
      operators: [
        { label: 'is', value: 'custom:team is' },
        {
          label: 'is not',
          value: 'custom:team is not',
        },
      ],
      field: <TeamSelect />,
    },
    {
      key: 'skill_id',
      type: 'choice',
      label: 'Linked Skill',
      choices: skillChoices,
      nullable: true,
    },
    {
      key: 'answers',
      type: 'textarray',
      label: 'Answer Message',
    },
    {
      key: 'samples',
      type: 'textarray',
      label: 'Sample Questions',
    },
    {
      key: 'samples_count',
      type: 'number',
      label: 'Number of Sample Questions',
    },
    {
      key: 'times_used',
      type: 'number',
      label: 'Times Used',
    },
    {
      key: 'success_rate',
      type: 'custom',
      label: 'Success Rate',
      operators: [
        { label: 'is', value: 'custom:success_rate is' },
        {
          label: 'is greater than',
          value: 'custom:success_rate is greater than',
        },
        { label: 'is less than', value: 'custom:success_rate is less than' },
      ],
      field: (
        <NormalisedGeckoField
          type={GECKO_CONSTS.FIELD_TYPE_NUMBER}
          min={0}
          max={100}
          affix="%"
        />
      ),
    },
    {
      key: 'createdAt',
      type: 'date',
      label: 'Created at',
    },
    {
      key: 'updatedAt',
      type: 'date',
      label: 'Updated at',
    },
    {
      key: 'last_time_used',
      type: 'date',
      label: 'Last Time Used',
    },
    {
      key: 'bot_tag_ids',
      type: 'custom',
      label: 'Tags',
      choices: tagChoices,
      field_type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
      nullable: true,
      operators: [
        { label: 'contains', value: 'textarray:is' },
        { label: 'not contains', value: 'textarray:is not' },
        { label: 'is blank', value: 'textarray:is blank' },
        { label: 'is not blank', value: 'textarray:is not blank' },
      ],
    },
  ];

  return (
    <React.Fragment>
      <DeltaSummary botId={botId} importId={bot.bot_import_id} />

      <div className="Answers">
        <BotTabTableContainer
          title="Answers"
          searchValue={search}
          headerChild={renderHeaderChild()}
        >
          <Collapse in={showFilters}>
            <div>
              {showFilters ? (
                <FilterBuilder
                  filters={filters}
                  fields={fields}
                  onChange={setPickedFilters}
                />
              ) : null}
            </div>
          </Collapse>

          {responses && responses.length ? (
            <div
              className={classNames('gecko-card-body', {
                'gecko-card-body--large': responses.length > 8,
                'gecko-card-body--small': responses.length <= 8,
              })}
            >
              <GeckoTable
                name="Answers"
                cols={cols}
                rows={responses}
                pagination={pagination}
                rowHandler={rowHandler}
              />
              <GeckoPagination pagination={pagination} />
            </div>
          ) : search ? (
            <div className="BotTabTableContainer-table-body gecko-card-body padded">
              <GeckoAlert type={GECKO_CONSTS.ALERT_TYPE_WARNING}>
                No results found for the search term "{search}".
              </GeckoAlert>
            </div>
          ) : (
            <div className="BotTabTableContainer-table-body gecko-card-body padded">
              <GeckoAlert type={GECKO_CONSTS.ALERT_TYPE_WARNING}>
                Your bot hasn't got any answers yet.
              </GeckoAlert>
            </div>
          )}
        </BotTabTableContainer>
      </div>
    </React.Fragment>
  );
}

export default Answers;
